import * as React from 'react';
import { registerComponent, useMatchLargeViewport } from '@avensia/excite';
import { Button as ButtonComponent, ButtonLink as ButtonLinkComponent } from 'webapp/components/button/button';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { Theme, isNordicNest, isSvenssons } from 'webapp/theme';
import { FieldSize, fieldXSpacing } from 'webapp/components/field-size';
import { styled } from '@glitz/react';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { type Decorators } from '@avensia/excite-contentful/Document';
import { linkWithoutQueryParams } from 'webapp/link-url-decorator';

export default registerComponent<NordicNest.Api.Button>(ButtonBlock);

export function ButtonBlock(props: NordicNest.Api.Button) {
  const { textColor, textColorHover, backgroundColor, backgroundColorHover, ghost = false, thin = false } = props;
  const content = <Document styleDecorators={styles} elements={props.text} decorators={decorators} />;
  const isLargeViewport = useMatchLargeViewport();

  let buttonSize;
  if (thin) {
    buttonSize = isSvenssons ? FieldSize.Small : FieldSize.XSmall;
  } else if (isNordicNest) {
    buttonSize = isLargeViewport ? FieldSize.Small : FieldSize.XSmall;
  } else if (isSvenssons) {
    buttonSize = isLargeViewport ? FieldSize.Large : FieldSize.Default;
  }

  const overrideButtonStyle = Boolean(ghost || backgroundColor || textColor || textColorHover || backgroundColorHover);

  const style = React.useMemo(
    () =>
      ghost
        ? styled(
            createButtonHoverDecorator({
              color: textColorHover,
              backgroundColor: backgroundColorHover,
              border: { xy: { width: 1, style: 'solid', color: backgroundColorHover } },
              boxShadow: `inset 0 0 0 1px ${backgroundColorHover}`,
            }),
            {
              color: textColor,
              backgroundColor: 'transparent',
              border: { xy: { width: 1, style: 'solid', color: backgroundColor } },
              boxShadow: `inset 0 0 0 1px ${backgroundColor}`,
            },
          )
        : styled(
            createButtonHoverDecorator({
              color: textColorHover,
              backgroundColor: backgroundColorHover,
              border: { xy: { color: backgroundColorHover } },
            }),
            {
              color: textColor,
              backgroundColor,
              border: { xy: { color: backgroundColor } },
            },
          ),
    [ghost, textColorHover, backgroundColorHover, textColor, backgroundColor],
  );

  return props.link ? (
    <ButtonLink
      size={buttonSize}
      css={overrideButtonStyle && style}
      as={Theme.LinkButton}
      {...linkWithoutQueryParams(props.link)}
    >
      {content}
    </ButtonLink>
  ) : (
    <Button size={buttonSize} css={overrideButtonStyle && style} as={Theme.LinkButton}>
      {content}
    </Button>
  );
}

const smallXPaddingDecorator = styled({
  padding: {
    x: isNordicNest ? fieldXSpacing(FieldSize.Small) : fieldXSpacing(FieldSize.Default),
  },
});

const noTextTransformDecorator = styled({
  textTransform: 'none',
});

//This styling was on the paragraph element that has been removed, see comment below
const defaultFontSizeDecorator = styled(
  !isSvenssons ? { fontSize: t => t.sharedFontSize.xs, lineHeight: t => 24 / t.sharedFontSize.xs } : {},
);

const buttonDecorator = styled({
  width: '100%',
  textAlign: 'center',
  display: 'block',
});

const ButtonLink = styled(
  ButtonLinkComponent,
  smallXPaddingDecorator,
  noTextTransformDecorator,
  defaultFontSizeDecorator,
  buttonDecorator,
);
const Button = styled(
  ButtonComponent,
  smallXPaddingDecorator,
  noTextTransformDecorator,
  defaultFontSizeDecorator,
  buttonDecorator,
);

const noMargin = styled(
  createMinLargeViewportDecorator({
    marginBottom: 0,
  }),
  { marginBottom: 0 },
);
const styles: StyleDecorators = {
  h1: noMargin,
  h2: noMargin,
  h3: noMargin,
  h4: noMargin,
  h5: noMargin,
  h6: noMargin,
  pre: noMargin,
};

//Since buttonBlock is often used inside a paragraph, we need to make sure that we don't use a paragraph inside a paragraph
const decorators: Decorators = {
  p: styled(({ children }) => <>{children}</>),
};
